import { useEffect, useState } from "react";
import { api, getStored, storeSession } from "./api";
import type { Session } from "./types";
import Chat from "./Chat";
import Legal from "./Legal";
const words = [
  "SwiftFox",
  "BlueBoar",
  "FlyingFish",
  "BrightOtter",
  "QuietPanda",
  "LuckyGecko",
  "MerryRaven",
  "SilverLynx",
  "CosmicKoala",
];
const randomName = () => words[Math.floor(Math.random() * words.length)];
export default function App() {
  const path = location.pathname.split("/").filter(Boolean);
  const legal = path[0] === "legal" ? path[1] : null;
  const code = path[0] === "c" ? path[1] : null;
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!!code);
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  const [nickname, setNickname] = useState(randomName());
  const [pin, setPin] = useState("");
  const [usePin, setUsePin] = useState(false);
  const [theme, setTheme] = useState(localStorage.theme || "auto");
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (code) {
      document.title = "Private temporary chat — TempChat.org";
      robots.content = "noindex,nofollow,noarchive,nosnippet";
      canonical.removeAttribute("href");
      return;
    }
    robots.content = "index,follow,max-image-preview:large";
    const legalMeta: Record<string, [string, string]> = {
      privacy: ["Privacy Policy — TempChat.org", "https://tempchat.org/legal/privacy"],
      terms: ["Terms of Service — TempChat.org", "https://tempchat.org/legal/terms"],
      cookies: ["Cookie Policy — TempChat.org", "https://tempchat.org/legal/cookies"],
    };
    const selected = legal ? legalMeta[legal] : undefined;
    document.title = selected?.[0] || "TempChat.org — Free Anonymous 10-Minute Group Chat";
    canonical.href = selected?.[1] || "https://tempchat.org/";
  }, [code, legal]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.theme = theme;
  }, [theme]);
  useEffect(() => {
    if (!code) return;
    const saved = getStored(code);
    (async () => {
      try {
        const p = await api.preview(code);
        setPreview(p.room);
        if (saved?.sessionToken) {
          const restored = await api.restore(code, saved.sessionToken);
          setSession({
            ...restored,
            sessionToken: saved.sessionToken,
            adminToken: saved.adminToken,
          });
        } else setNickname((await api.name(code)).nickname);
      } catch (e: any) {
        setError(
          e.code === "ROOM_NOT_FOUND"
            ? "This chat has expired or does not exist."
            : "Could not open this chat.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);
  if (legal) return <Legal page={legal} />;
  if (session)
    return <Chat initial={session} onExpired={() => setSession(null)} />;
  const join = async () => {
    if (!code) return;
    setLoading(true);
    setError("");
    try {
      const s = await api.join(code, nickname, pin);
      storeSession(s.room.inviteCode, s);
      setSession(s);
    } catch (e: any) {
      setError(
        (
          {
            INVALID_PIN: "Wrong PIN. Try again.",
            PIN_LOCKED: "Too many attempts. Try again in 10 minutes.",
            NICKNAME_TAKEN: "That name is already in use.",
            ROOM_FULL: "This chat is full.",
            ROOM_LOCKED: "This chat is closed to new people.",
          } as any
        )[e.code] || e.message,
      );
    } finally {
      setLoading(false);
    }
  };
  const create = async () => {
    setLoading(true);
    setError("");
    try {
      const s = await api.create(nickname, usePin ? pin : undefined);
      storeSession(s.room.inviteCode, s);
      if (usePin)
        localStorage.setItem(`tempchat:pin:${s.room.inviteCode}`, pin);
      history.pushState({}, "", `/c/${s.room.inviteCode}`);
      setSession(s);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="landing">
      <nav>
        <a className="brand" href="/">
          <span>⌁</span> TempChat.org
        </a>
        <button
          className="icon-btn"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label="Toggle theme"
        >
          {theme === "dark" ? "☀" : "◐"}
        </button>
      </nav>
      <section className="hero">
        <div className="eyebrow">NO ACCOUNT · NO HISTORY</div>
        <h1>
          Talk now.
          <br />
          <em>Leave no trace.</em>
        </h1>
        <p>
          A beautiful group chat that vanishes ten minutes after your first
          guest arrives.
        </p>
        <div className="card">
          {loading ? (
            <div className="loader">Opening your chat…</div>
          ) : error && !preview ? (
            <>
              <div className="expired-icon">⌛</div>
              <h2>Chat unavailable</h2>
              <p className="error">{error}</p>
              <a className="primary" href="/">
                Create a new chat
              </a>
            </>
          ) : code && preview ? (
            <>
              <div className="room-badge">{preview.hasPin ? "🔒" : "⚡"}</div>
              <h2>{preview.title}</h2>
              <p className="muted">
                {preview.status === "WAITING"
                  ? "The timer starts when you join."
                  : "This chat is already ticking."}
              </p>
              <label>
                Your temporary name
                <input
                  value={nickname}
                  maxLength={24}
                  onChange={(e) => setNickname(e.target.value)}
                />
              </label>
              {preview.hasPin && (
                <label>
                  4-digit PIN
                  <input
                    inputMode="numeric"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  />
                </label>
              )}
              {error && <p className="error">{error}</p>}
              <button
                className="primary"
                disabled={
                  nickname.length < 2 || (preview.hasPin && pin.length !== 4)
                }
                onClick={join}
              >
                Join chat <span>→</span>
              </button>
            </>
          ) : (
            <>
              <h2>Create an instant chat</h2>
              <label>
                Your temporary name
                <input
                  value={nickname}
                  maxLength={24}
                  onChange={(e) => setNickname(e.target.value)}
                />
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={usePin}
                  onChange={(e) => setUsePin(e.target.checked)}
                />
                <span>Add a 4-digit PIN</span>
              </label>
              {usePin && (
                <input
                  aria-label="PIN"
                  className="pin"
                  inputMode="numeric"
                  placeholder="0000"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                />
              )}{" "}
              {error && <p className="error">{error}</p>}
              <button
                className="primary"
                disabled={nickname.length < 2 || (usePin && pin.length !== 4)}
                onClick={create}
              >
                Create temporary chat <span>→</span>
              </button>
              <small>Free · Up to 10 people · No registration</small>
              <a
                className="wwchats-link"
                href="https://wwchats.com/?utm_source=tempchat&utm_medium=referral&utm_campaign=need_more"
              >
                Need more? Create lasting rooms on WWChats →
              </a>
            </>
          )}
        </div>
      </section>
      <section className="features">
        <article>
          <b>10:00</b>
          <h3>Gone means gone</h3>
          <p>Messages and files are deleted when time runs out.</p>
        </article>
        <article>
          <b>◌</b>
          <h3>Come as you are</h3>
          <p>No signup. We’ll even give you a temporary identity.</p>
        </article>
        <article>
          <b>⌁</b>
          <h3>Share in a tap</h3>
          <p>Send the short link anywhere and start talking.</p>
        </article>
      </section>
      <footer>
        <span>© 2026 OPSoft Inc</span>
        <span>
          <a href="/legal/privacy">Privacy</a>
          <a href="/legal/terms">Terms</a>
          <a href="/legal/cookies">Cookies</a>
        </span>
      </footer>
      <CookieConsent />
    </main>
  );
}
function CookieConsent() {
  const [v, setV] = useState(localStorage.analyticsConsent || "");
  if (v) return null;
  const choose = (x: string) => {
    localStorage.analyticsConsent = x;
    setV(x);
    if (x === "yes") location.reload();
  };
  return (
    <div className="cookies">
      <p>
        We use optional Google Analytics cookies to improve TempChat. Chatting
        works without them.
      </p>
      <div>
        <button onClick={() => choose("no")}>Reject</button>
        <button onClick={() => choose("yes")}>Accept analytics</button>
      </div>
    </div>
  );
}
